import { API_BASE_URL } from '../config/constants';
import { backofficeAuthService } from './backofficeAuthService';

export interface ApiResponse<T = any> {
  data: T;
  status: number;
  statusText: string;
}

export interface ApiError {
  detail: string;
  status: number;
}

class BackofficeApiService {
  private baseUrl = API_BASE_URL;

  private async makeRequest(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<ApiResponse> {
    const token = backofficeAuthService.getAccessToken();
    
    const headers = {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
      ...options.headers,
    };

    const config: RequestInit = {
      ...options,
      headers,
    };

    let response = await fetch(`${this.baseUrl}${endpoint}`, config);

    // Handle 401 - try to refresh token
    if (response.status === 401 && token) {
      try {
        await backofficeAuthService.refreshToken();
        const newToken = backofficeAuthService.getAccessToken();
        
        if (newToken) {
          const refreshedHeaders = {
            ...headers,
            Authorization: `Bearer ${newToken}`,
          };
          
          response = await fetch(`${this.baseUrl}${endpoint}`, {
            ...config,
            headers: refreshedHeaders,
          });
        }
      } catch (refreshError) {
        backofficeAuthService.logout();
        throw new Error('Session expired. Please login again.');
      }
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `HTTP ${response.status}: ${response.statusText}`);
    }

    const data = await response.json();
    
    return {
      data,
      status: response.status,
      statusText: response.statusText,
    };
  }

  async get(endpoint: string, options: { params?: Record<string, any> } = {}): Promise<ApiResponse> {
    let url = endpoint;
    
    if (options.params) {
      const searchParams = new URLSearchParams();
      Object.entries(options.params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          searchParams.append(key, String(value));
        }
      });
      
      const queryString = searchParams.toString();
      if (queryString) {
        url += `?${queryString}`;
      }
    }

    return this.makeRequest(url, {
      method: 'GET',
    });
  }

  async post(endpoint: string, data?: any, options?: { params?: Record<string, any> }): Promise<ApiResponse> {
    let url = endpoint;
    
    if (options?.params) {
      const searchParams = new URLSearchParams();
      Object.entries(options.params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          searchParams.append(key, String(value));
        }
      });
      
      const queryString = searchParams.toString();
      if (queryString) {
        url += `?${queryString}`;
      }
    }

    return this.makeRequest(url, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async put(endpoint: string, data?: any, options?: { params?: Record<string, any> }): Promise<ApiResponse> {
    let url = endpoint;
    
    if (options?.params) {
      const searchParams = new URLSearchParams();
      Object.entries(options.params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          searchParams.append(key, String(value));
        }
      });
      
      const queryString = searchParams.toString();
      if (queryString) {
        url += `?${queryString}`;
      }
    }

    return this.makeRequest(url, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async patch(endpoint: string, data?: any): Promise<ApiResponse> {
    return this.makeRequest(endpoint, {
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async delete(endpoint: string): Promise<ApiResponse> {
    return this.makeRequest(endpoint, {
      method: 'DELETE',
    });
  }

  // Upload methods for handling file uploads
  async upload(endpoint: string, formData: FormData): Promise<ApiResponse> {
    const token = backofficeAuthService.getAccessToken();
    
    const headers: Record<string, string> = {};
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      method: 'POST',
      headers,
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `HTTP ${response.status}: ${response.statusText}`);
    }

    const data = await response.json();
    
    return {
      data,
      status: response.status,
      statusText: response.statusText,
    };
  }
}

export const backofficeApi = new BackofficeApiService();